/**
 * Image URL utility functions
 * Centralized functions for generating image URLs from config
 */
import config from '../config';

/**
 * Get the full URL for an uploaded image
 * @param {string} imagePath - The image path from the database (e.g., "uploads/F/image.jpg" or "F\\image.jpg")
 * @returns {string|null} - Full URL to the image or null if path is invalid
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // Clean the path (replace backslashes with forward slashes)
  let cleanPath = imagePath.replace(/\\/g, '/');

  // Remove leading "uploads/" if it exists to avoid double "uploads/uploads/"
  cleanPath = cleanPath.replace(/^uploads\//, '');

  // Construct full URL
  return `${config.imageBaseURL}${config.uploadsPath}/${cleanPath}`;
};

/**
 * Coerce an image entry to a string path (handles API returning objects like { path, url } or strings)
 * @param {string|Object} image - Image path string or object with path/url/src
 * @returns {string|null} - Path string or null
 */
const toImagePath = (image) => {
  if (typeof image === 'string') return image;
  if (image && typeof image === 'object') {
    const path = image.path ?? image.url ?? image.src;
    return typeof path === 'string' ? path : null;
  }
  return null;
};

/**
 * Get optimized image URL from an array of images
 * Prioritizes PICFRONT3D images, then PICFRONT, then falls back to first available image
 * @param {Array<string|Object>} images - Array of image paths or objects with path/url
 * @returns {string|null} - Full URL to the optimized image or null if no images
 */
export const getOptimizedImageUrl = (images) => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return null;
  }

  const paths = images.map(toImagePath).filter(Boolean);
  if (paths.length === 0) return null;

  // Look for PICFRONT3D image first (preferred)
  const picFront3DImage = paths.find((path) => path.includes('PICFRONT3D'));
  if (picFront3DImage) {
    return getImageUrl(picFront3DImage);
  }

  // Look for PICFRONT image as second choice
  const picFrontImage = paths.find((path) => path.includes('PICFRONT'));
  if (picFrontImage) {
    return getImageUrl(picFrontImage);
  }

  // Fall back to first image
  return getImageUrl(paths[0]);
};

/**
 * Get product image URL with fallback logic
 * @param {Object} product - Product object with images property
 * @param {string} imageSize - Image size key ('F', etc.) - defaults to 'F'
 * @returns {string|null} - Full URL to the product image or null
 */
export const getProductImageUrl = (product, imageSize = 'F') => {
  if (!product || !product.hasPhoto || !product.images || !product.images[imageSize]) {
    return null;
  }

  const images = product.images[imageSize];
  if (!Array.isArray(images) || images.length === 0) {
    return null;
  }

  const paths = images.map(toImagePath).filter(Boolean);
  if (paths.length === 0) return null;

  // Look for PICFRONT3D image first
  const picFront3DImage = paths.find((path) => path.includes('PICFRONT3D'));
  const imageToDisplay = picFront3DImage || paths[0];

  return getImageUrl(imageToDisplay);
};

/**
 * Normalize image array from different API response structures
 * Handles both:
 * - article.images as direct array (search results format)
 * - article.images.F as array (product listing / details)
 * - product.images as direct array
 * @param {Object} item - Article or product object with images property
 * @param {string} imageSize - Image size key ('F', etc.) - defaults to 'F'
 * @returns {Array<string>} - Normalized array of image paths
 */
export const normalizeImageArray = (item, imageSize = 'F') => {
  if (!item || !item.images) {
    return [];
  }

  // Case 1: images is directly an array (search results format)
  if (Array.isArray(item.images)) {
    return item.images;
  }

  // Case 2: images is an object with size property (e.g., images.F)
  if (item.images[imageSize] && Array.isArray(item.images[imageSize])) {
    return item.images[imageSize];
  }

  // Case 3: images is an object but no size property found
  return [];
};

/**
 * Get article image URL (for articles with images.F array or direct images array)
 * @param {Object} article - Article object with images property
 * @param {string} imageSize - Image size key ('F', etc.) - defaults to 'F'
 * @returns {string|null} - Full URL to the article image or null
 */
export const getArticleImageUrl = (article, imageSize = 'F') => {
  if (!article || !article.images) {
    return null;
  }

  const images = normalizeImageArray(article, imageSize);
  if (images.length === 0) {
    return null;
  }

  return getOptimizedImageUrl(images);
};

/**
 * Reorder images to prioritize 3D images
 * @param {Array<string>} images - Array of image paths
 * @returns {Array<string>} - Reordered array with 3D images first
 */
export const reorderImages = (images) => {
  if (!images || !Array.isArray(images)) {
    return [];
  }

  const paths = images.map(toImagePath).filter(Boolean);
  const picFront3D = paths.find((path) => path.includes('PICFRONT3D'));
  const picBack3D = paths.find((path) => path.includes('PICBACK3D'));
  const otherImages = paths.filter(
    (path) => !path.includes('PICFRONT3D') && !path.includes('PICBACK3D')
  );

  const orderedImages = [];
  if (picFront3D) orderedImages.push(picFront3D);
  if (picBack3D) orderedImages.push(picBack3D);
  return [...orderedImages, ...otherImages];
};

/**
 * Get multiple image URLs from an array of image paths
 * Useful for 360 viewer or image galleries
 * @param {Array<string>} images - Array of image paths
 * @returns {Array<string>} - Array of full image URLs
 */
export const getImageUrls = (images) => {
  if (!images || !Array.isArray(images)) {
    return [];
  }

  return images.map((img) => getImageUrl(toImagePath(img))).filter(Boolean);
};
